import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { FileText, Eye, Save, Globe, GlobeLock, ArrowLeft } from 'lucide-react';
import { AdminLayout } from '../../components/admin';
import { Button, Input } from '../../components/ui';
import { toast } from '../../components/ui/Toaster';
import { eventService } from '../../services/eventService';
import { apiGet, apiPost, apiPut, apiPatch } from '../../services/api';
import { getPreviewLandingPageUrl } from '../../utils/landingPageUrls';
import type { Event } from '../../types';

interface LandingPage {
  id: number;
  eventId: number;
  slug: string;
  metaDescription?: string;
  section1Title?: string;
  section1Content?: string;
  section2Title?: string;
  section2Content?: string;
  section3Title?: string;
  section3Content?: string;
  section4Title?: string;
  section4Content?: string;
  isPublished: boolean;
  createdAt: string;
  updatedAt: string;
  event: {
    id: number;
    title: string;
    slug: string;
    startDate: string;
    location: string | null;
    image: string | null;
  };
}

const EventLandingPageEditor: React.FC = () => {
  const { eventId } = useParams<{ eventId: string }>();
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [event, setEvent] = useState<Event | null>(null);
  const [landingPage, setLandingPage] = useState<LandingPage | null>(null);
  const [isNewPage, setIsNewPage] = useState(false);
  
  // Form data
  const [formData, setFormData] = useState({
    metaDescription: '',
    section1Title: '',
    section1Content: '',
    section2Title: '',
    section2Content: '',
    section3Title: '',
    section3Content: '',
    section4Title: '',
    section4Content: ''
  });

  useEffect(() => {
    if (eventId) {
      loadData();
    }
  }, [eventId]);

  const loadData = async () => {
    try {
      setLoading(true);
      
      // Get event data first
      const eventData = await eventService.getEvent(parseInt(eventId!));
      setEvent(eventData);
      
      // Try to get existing landing page
      try {
        const landingPageData = await apiGet<LandingPage>(`/admin/events/${eventId}/landing-page`);
        setLandingPage(landingPageData);
        setFormData({
          metaDescription: landingPageData.metaDescription || '',
          section1Title: landingPageData.section1Title || '',
          section1Content: landingPageData.section1Content || '',
          section2Title: landingPageData.section2Title || '',
          section2Content: landingPageData.section2Content || '',
          section3Title: landingPageData.section3Title || '',
          section3Content: landingPageData.section3Content || '',
          section4Title: landingPageData.section4Title || '',
          section4Content: landingPageData.section4Content || ''
        });
        setIsNewPage(false);
      } catch (error: any) {
        // If 404, no landing page exists yet
        if (error.response?.status === 404) {
          setIsNewPage(true);
          setFormData({
            metaDescription: `Ga je alleen naar ${eventData.title}? Vind je perfecte festivalgroep met Solofix!`,
            section1Title: '',
            section1Content: '',
            section2Title: '',
            section2Content: '',
            section3Title: '',
            section3Content: '',
            section4Title: '',
            section4Content: ''
          });
        } else {
          console.error('Error loading landing page:', error);
          toast.error('Fout', 'Kon landing pagina niet laden');
          setIsNewPage(true);
        }
      }
    } catch (error) {
      console.error('Error loading event:', error);
      toast.error('Fout', 'Kon evenement niet laden');
      navigate('/admin/events');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async (publish: boolean = false) => {
    try {
      setSaving(true);
      
      let savedLandingPage: LandingPage;
      
      if (isNewPage) {
        savedLandingPage = await apiPost<LandingPage>(`/admin/events/${eventId}/landing-page`, formData);
      } else {
        savedLandingPage = await apiPut<LandingPage>(`/admin/landing-pages/${landingPage?.id}`, formData);
      }
      
      setLandingPage(savedLandingPage);
      setIsNewPage(false);
      
      // If we want to publish, make separate call
      if (publish && !savedLandingPage.isPublished) {
        const publishedLandingPage = await apiPatch<LandingPage>(`/admin/landing-pages/${savedLandingPage.id}/publish`, {});
        setLandingPage(publishedLandingPage);
        toast.success('Succes', 'Landing pagina opgeslagen en gepubliceerd');
      } else {
        toast.success('Succes', 'Landing pagina opgeslagen');
      }
    } catch (error: any) {
      console.error('Error saving landing page:', error);
      toast.error('Fout', error.message || 'Er ging iets mis bij het opslaan');
    } finally {
      setSaving(false);
    }
  };

  const handleTogglePublish = async () => {
    if (!landingPage) {
      toast.error('Fout', 'Sla eerst de pagina op');
      return;
    }

    try {
      const updatedLandingPage = await apiPatch<LandingPage>(`/admin/landing-pages/${landingPage.id}/publish`, {});
      setLandingPage(updatedLandingPage);
      toast.success('Succes', updatedLandingPage.isPublished ? 'Landing pagina gepubliceerd' : 'Landing pagina gedepubliceerd');
    } catch (error: any) {
      console.error('Error toggling publish status:', error);
      toast.error('Fout', error.message || 'Er ging iets mis bij het wijzigen van de status');
    }
  };

  const getPreviewUrl = () => {
    if (!landingPage?.slug) return '#';
    return getPreviewLandingPageUrl(landingPage.slug);
  };

  if (loading) {
    return (
      <AdminLayout title="Landing Pagina Editor">
        <div className="flex items-center justify-center h-64">
          <div className="text-fg-muted">Laden...</div>
        </div>
      </AdminLayout>
    );
  }

  if (!event) {
    return (
      <AdminLayout title="Landing Pagina Editor">
        <div className="flex items-center justify-center h-64">
          <div className="text-neon-pink-soft">Evenement niet gevonden</div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout 
      title={`Landing Pagina - ${event.title}`}
      subtitle={isNewPage ? 'Nieuwe landing pagina aanmaken' : 'Landing pagina bewerken'}
    >
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <Button
            variant="outline"
            onClick={() => navigate('/admin/events')}
            className="flex items-center"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Terug naar evenementen
          </Button>
          
          <div className="flex items-center space-x-3">
            {landingPage && (
              <>
                <Button
                  variant="outline"
                  onClick={() => window.open(getPreviewUrl(), '_blank')}
                  disabled={!landingPage.isPublished}
                  className="flex items-center"
                >
                  <Eye className="h-4 w-4 mr-2" />
                  Preview
                </Button>
                
                <Button
                  variant={landingPage.isPublished ? "secondary" : "primary"}
                  onClick={handleTogglePublish}
                  className="flex items-center"
                >
                  {landingPage.isPublished ? (
                    <>
                      <GlobeLock className="h-4 w-4 mr-2" />
                      Unpublish
                    </>
                  ) : (
                    <>
                      <Globe className="h-4 w-4 mr-2" />
                      Publish
                    </>
                  )}
                </Button>
              </>
            )}
            
            <Button
              onClick={() => handleSave()}
              loading={saving}
              className="flex items-center"
            >
              <Save className="h-4 w-4 mr-2" />
              Opslaan
            </Button>
          </div>
        </div>

        {/* Status indicator */}
        {landingPage && (
          <div className="sf-card p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="sf-label-mono">Status</p>
                <p className="mt-1 text-sm text-fg-body">
                  {landingPage.isPublished ? (
                    <span className="sf-badge-lime">Gepubliceerd</span>
                  ) : (
                    <span className="sf-badge-cyan">Concept</span>
                  )}
                </p>
              </div>
              {landingPage.isPublished && (
                <div>
                  <p className="sf-label-mono">Live URL</p>
                  <a
                    href={getPreviewUrl()}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="sf-link text-sm break-all"
                  >
                    {getPreviewUrl()}
                  </a>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Editor Form */}
        <div className="sf-card p-6 space-y-6">
          <h2 className="text-lg font-bold text-fg-bright flex items-center">
            <FileText className="h-5 w-5 mr-2 text-neon-cyan" />
            Content Editor
          </h2>

          {/* SEO */}
          <div>
            <label className="block text-sm font-semibold text-fg-soft mb-2">
              Meta Description (SEO)
            </label>
            <Input
              value={formData.metaDescription}
              onChange={(e) => setFormData({ ...formData, metaDescription: e.target.value })}
              placeholder="Korte beschrijving voor zoekmachines (max 160 karakters)"
              maxLength={160}
            />
            <p className="font-mono text-[11px] text-fg-meta mt-1">
              {formData.metaDescription.length}/160 karakters
            </p>
          </div>

          {/* Section 1 */}
          <div>
            <label className="block text-sm font-semibold text-fg-soft mb-2">
              Sectie 1: Wat is Solofix + CTA
            </label>
            <Input
              value={formData.section1Title}
              onChange={(e) => setFormData({ ...formData, section1Title: e.target.value })}
              placeholder="Titel voor sectie 1 (bijv. 'Wat is Solofix?')"
              className="mb-3"
              maxLength={100}
            />
            <textarea
              value={formData.section1Content}
              onChange={(e) => setFormData({ ...formData, section1Content: e.target.value })}
              placeholder="Leg uit wat Solofix is en waarom bezoekers zich moeten aanmelden (ca. 150 woorden)"
              className="sf-input"
              rows={6}
              maxLength={2000}
            />
            <p className="font-mono text-[11px] text-fg-meta mt-1">
              {formData.section1Content.length}/2000 karakters
            </p>
          </div>

          {/* Section 2 */}
          <div>
            <label className="block text-sm font-semibold text-fg-soft mb-2">
              Sectie 2: Over het evenement
            </label>
            <Input
              value={formData.section2Title}
              onChange={(e) => setFormData({ ...formData, section2Title: e.target.value })}
              placeholder="Titel voor sectie 2 (bijv. 'Over Lowlands 2025')"
              className="mb-3"
              maxLength={100}
            />
            <textarea
              value={formData.section2Content}
              onChange={(e) => setFormData({ ...formData, section2Content: e.target.value })}
              placeholder="Beschrijf het evenement en waarom het speciaal is (ca. 200 woorden)"
              className="sf-input"
              rows={6}
              maxLength={2000}
            />
            <p className="font-mono text-[11px] text-fg-meta mt-1">
              {formData.section2Content.length}/2000 karakters
            </p>
          </div>

          {/* Section 3 */}
          <div>
            <label className="block text-sm font-semibold text-fg-soft mb-2">
              Sectie 3: Hoe Solofix werkt voor dit evenement
            </label>
            <Input
              value={formData.section3Title}
              onChange={(e) => setFormData({ ...formData, section3Title: e.target.value })}
              placeholder="Titel voor sectie 3 (bijv. 'Zo werkt Solofix voor Lowlands')"
              className="mb-3"
              maxLength={100}
            />
            <textarea
              value={formData.section3Content}
              onChange={(e) => setFormData({ ...formData, section3Content: e.target.value })}
              placeholder="Leg uit hoe Solofix specifiek voor dit evenement kan helpen (ca. 200 woorden)"
              className="sf-input"
              rows={6}
              maxLength={2000}
            />
            <p className="font-mono text-[11px] text-fg-meta mt-1">
              {formData.section3Content.length}/2000 karakters
            </p>
          </div>

          {/* Section 4 */}
          <div>
            <label className="block text-sm font-semibold text-fg-soft mb-2">
              Sectie 4: Laatste oproep tot actie
            </label>
            <Input
              value={formData.section4Title}
              onChange={(e) => setFormData({ ...formData, section4Title: e.target.value })}
              placeholder="Titel voor sectie 4 (bijv. 'Klaar om je festivalgroep te vinden?')"
              className="mb-3"
              maxLength={100}
            />
            <textarea
              value={formData.section4Content}
              onChange={(e) => setFormData({ ...formData, section4Content: e.target.value })}
              placeholder="Overtuig bezoekers om zich definitief aan te melden (ca. 150 woorden)"
              className="sf-input"
              rows={6}
              maxLength={2000}
            />
            <p className="font-mono text-[11px] text-fg-meta mt-1">
              {formData.section4Content.length}/2000 karakters
            </p>
          </div>
        </div>

        {/* Save button at bottom */}
        <div className="flex justify-end space-x-3">
          <Button
            variant="outline"
            onClick={() => navigate('/admin/events')}
          >
            Annuleren
          </Button>
          
          <Button
            onClick={() => handleSave()}
            loading={saving}
            className="flex items-center"
          >
            <Save className="h-4 w-4 mr-2" />
            Opslaan
          </Button>
        </div>
      </div>
    </AdminLayout>
  );
};

export default EventLandingPageEditor;